<template>
    <div>
        <div class="row">
            <div class="col">
                <ul class="pagination mb-0" v-if="total > 1">
                    <!--Prev Button-->
                    <li :class="{ disabled: onFirstPage }" class="page-item">
                        <NuxtLink @click.prevent="setPage(currentPage - 1)" :to="prevPageHref" class="page-link" rel="prev" aria-label="Previous">
                            <span aria-hidden="true">&laquo;</span>
                        </NuxtLink>
                    </li>

                    <!--Page Buttons-->
                    <li v-for="paginator in paginators"
                        :class="{ active: paginator.value === currentPage, disabled: !paginator.enable}"
                        class="page-item">
                        <NuxtLink @click.prevent="setPage(paginator.value)" class="page-link" :to="pageHref(paginator.value)">
                            <span>{{ paginator.value }}</span>
                        </NuxtLink>
                    </li>

                    <!--Next Button-->
                    <li v-if="!onLastPage" :class="{ disabled: onLastPage }" class="page-item">
                        <NuxtLink @click.prevent="setPage(currentPage + 1)" :to="nextPageHref" class="page-link" rel="next" aria-label="Next">
                            <span aria-hidden="true">&raquo;</span>
                        </NuxtLink>
                    </li>
                </ul>
            </div>

            <div v-if="enablePerPageSelect && (total > 1 || itemsPerPage !== perPage)" class="col-auto">
                <div class="input-group">
                    <div class="input-group-text">{{ $t('items') }}</div>
                    <select v-model="itemsPerPage" class="form-select-sm" aria-label="Items" tabindex="-1"
                            @change="updateItemsPerPage">
                        <option v-for="option in itemsPerPageOptions" :key="option" :value="option">
                            {{ option }}
                        </option>
                    </select>
                </div>
            </div>

            <LoadingSpinner :is-loading="isLoading"/>
        </div>
    </div>
</template>

<script>
import LoadingSpinner from "./LoadingSpinner.vue";

export default {
    components: {LoadingSpinner},
    props: {
        modelValue: {
            type: Number,
            default: 1,
            validator: val => val > 0,
        },
        value: {
            type: Number,
            default: 1,
            validator: val => val > 0,
        },
        total: {
            type: Number,
            required: true,
            default: 1
        },
        perPage: {
            type: Number,
            required: false,
            default: 10
        },
        eachSide: {
            type: Number,
            default: 1,
            validator: val => val >= 0,
        },
        onChange: {
            type: Function,
            default: () => {
            },
        },
        enablePerPageSelect: Boolean,
        itemsPerPageOptions: {
            type: Array,
            default: () => [10, 25, 50, 100],
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        scroll: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            itemsPerPage: useRoute().query.perPage || this.perPage,
        };
    },
    computed: {
        firstPage() {
            return 1;
        },
        lastPage() {
            return this.total;
        },
        onFirstPage() {
            return this.currentPage === this.firstPage;
        },
        onLastPage() {
            return this.currentPage >= this.lastPage;
        },
        currentPage() {
            return this.value;
        },
        prevPageHref() {
            return this.pageHref(this.currentPage - 1);
        },
        nextPageHref() {
            return this.pageHref(this.currentPage + 1);
        },
        paginators() {
            const paginators = [];
            const total = this.total;
            const currentPage = this.currentPage;
            const eachSide = this.eachSide;

            if (total <= 2 * eachSide + 1) {
                for (let i = 1; i <= total; i++) {
                    paginators.push({ value: i, enable: true });
                }
            } else {
                if (currentPage > 1 + eachSide + 1) {
                    paginators.push({ value: this.firstPage, enable: true });
                    paginators.push({ value: '...', enable: false });
                }

                let start = Math.max(this.firstPage, currentPage - eachSide);
                let end = Math.min(total, currentPage + eachSide);
                for (let i = start; i <= end; i++) {
                    paginators.push({ value: i, enable: true });
                }

                if (currentPage < total - eachSide - 1) {
                    paginators.push({ value: '...', enable: false });
                    paginators.push({ value: this.lastPage, enable: true });
                }
            }

            return paginators;
        },
    },
    methods: {
        nextPage() {
            this.setPage(this.currentPage + 1);
        },
        prevPage() {
            this.setPage(this.currentPage - 1);
        },
        setPage(targetPage) {
            if (typeof targetPage === 'number' && targetPage <= this.lastPage && targetPage >= this.firstPage) {
                this.$emit('update:modelValue', targetPage);
                this.onChange();
            }
            this.$emit('paginate', {
                page: targetPage,
                perPage: this.itemsPerPage
            })
        },
        updateItemsPerPage() {
            this.$emit('update:itemsPerPage', this.itemsPerPage);
            this.$emit('paginate', {
                page: this.currentPage,
                perPage: this.itemsPerPage
            })
        },
        pageHref(page) {
            const route = this.$route;
            const query = { ...route.query };

            if (page > 1) {
                query.page = page;
            } else {
                delete query.page;
            }

            return { path: route.path, query , state: {noScroll: !this.scroll}};
        }
    },
};
</script>
